import { type FormEvent, useCallback, useEffect, useState } from "react";

import { useAuth } from "../../app/auth";
import {
  type ChangeSet,
  type ItemAction,
  type RequalificationItem,
  changeSetInto,
  decideItem,
  shown,
} from "./profileClient";
import { useLatestRequest } from "./latest";

const ACTIONS: Record<RequalificationItem["status"], ItemAction[]> = {
  open: ["requalified", "not_affected"],
  requalified: ["reopen"],
  not_affected: ["reopen"],
};

function message(caught: unknown): string {
  return caught instanceof Error ? caught.message : String(caught);
}

function ItemDecision({ item, onDecided }: { item: RequalificationItem; onDecided: () => Promise<void> }) {
  const actions = ACTIONS[item.status];
  const [action, setAction] = useState<ItemAction>(actions[0] ?? "reopen");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    try {
      await decideItem(item.id, { action, reason: reason.trim() });
      setReason("");
      await onDecided();
    } catch (caught) {
      setError(message(caught));
    }
  }

  return (
    <form onSubmit={submit} aria-label={`Decide ${item.label}`}>
      <select
        aria-label="Outcome"
        value={action}
        onChange={(event) => setAction(event.target.value as ItemAction)}
      >
        {actions.map((a) => (
          <option key={a} value={a}>
            {a}
          </option>
        ))}
      </select>{" "}
      <input aria-label="Reason" value={reason} onChange={(event) => setReason(event.target.value)} />{" "}
      <button type="submit" disabled={!reason.trim()}>
        Record
      </button>
      {error && <p role="alert">{error}</p>}
    </form>
  );
}

/** What changed from the previous profile version, classified and explained, and the
 * requalification work it created (task 4070.8). Earlier versions and results are untouched. */
export function ChangesPanel({ snapshotId }: { snapshotId: string }) {
  const { hasPermission } = useAuth();
  const canDecide = hasPermission("engagement.manage") || hasPermission("mapping.certify");
  const [loaded, setLoaded] = useState(false);
  const [changeSet, setChangeSet] = useState<ChangeSet | null>(null);
  const [items, setItems] = useState<RequalificationItem[]>([]);
  const [all, setAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const begin = useLatestRequest();

  const load = useCallback(async () => {
    const current = begin();
    setError(null);
    try {
      const found = await changeSetInto(snapshotId);
      if (!current()) return;
      setChangeSet(found?.change_set ?? null);
      setItems(found?.items ?? []);
      setLoaded(true);
    } catch (caught) {
      if (current()) setError(message(caught));
    }
  }, [snapshotId, begin]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <p role="alert">{error}</p>;
  if (!loaded) return <p>Loading…</p>;
  if (!changeSet) {
    return <p>No change set leads to this version: it is the first profile version, or none was recorded.</p>;
  }
  const s = changeSet.summary;
  const shownChanges = changeSet.changes.filter((c) => all || c.impact !== "nonmaterial");
  return (
    <div>
      <p>
        From {changeSet.from_version} to {changeSet.to_version}: {s.material} material,{" "}
        {s.nonmaterial} nonmaterial, {s.unknown} unknown; {s.items} items to requalify.
        {s.historical_release_batches
          ? ` ${s.historical_release_batches} released batch(es) keep the version they used.`
          : ""}
      </p>
      <label>
        <input type="checkbox" checked={all} onChange={(event) => setAll(event.target.checked)} /> Show
        nonmaterial changes
      </label>
      <table className="grid">
        <caption>Changes</caption>
        <thead>
          <tr>
            <th>Impact</th>
            <th>Section</th>
            <th>Element</th>
            <th>Change</th>
            <th>Why it matters</th>
          </tr>
        </thead>
        <tbody>
          {shownChanges.map((c) => (
            <tr key={`${c.section}-${c.row_key}-${c.kind}`}>
              <td>{c.impact}</td>
              <td>{c.section}</td>
              <td>{c.element}</td>
              <td>
                {c.kind === "changed"
                  ? c.fields.map((f) => `${f.field}: ${shown(f.before)} → ${shown(f.after)}`).join("; ")
                  : c.kind}
              </td>
              <td>{c.explanation}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <table className="grid">
        <caption>Requalification work</caption>
        <thead>
          <tr>
            <th>Item</th>
            <th>Why</th>
            <th>Status</th>
            {canDecide && <th>Decision</th>}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td>{item.label}</td>
              <td>{item.reasons.join("; ")}</td>
              <td>{item.status}</td>
              {canDecide && (
                <td>
                  <ItemDecision key={`${item.id}-${item.status}`} item={item} onDecided={load} />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
